// Cómo genera el modo Redes — ADR-2026-10-01-modo-redes.
//
// No hay motor nuevo: Imagen es `generateFreeformImage` tal cual lo llama Prompt libre, y
// Video es `generateVideoFromPrompt` tal cual lo llama el Creador. Lo que cambia es a dónde
// va el resultado: una TANDA del lienzo del Agente (`agregarTanda`), que espera ids de
// `imagenes_generadas` ya con archivo. Por eso cada promesa termina con el preload, como
// hace `generarEnDiseno`.
//
// Las dos funciones devuelven lo que `agregarTanda` necesita además de la promesa (total,
// proporción, si es video, el `executionId` con el que se reanuda tras recargar) y, para el
// video, cuánto se estima que tarda, que es lo que pacea la barra del recuadro.

import type { ChatAttachment } from '../../agentAttachments';
import { signReferenceImage } from '../../agentAttachments';
import { getProductFullImageUrl } from '../../productsApi';
import { recordPromptHistory } from '../../promptHistory';
import { MAX_REFERENCIAS_VIDEO } from '../../referenciasDeGeneracion';
import { ratioDelFormatoElegido, resolverReferencias } from '../promptLibre/promptLibre';
import { formatSlugToAspect } from '../../agentGenerate';
import { escribirPromptDeVideo } from '../../../../lib/videoCreator';
import { idsDeVideo } from '../creadorVideo/creadorVideo';
import { estimarDuracionVideoMs } from '../../../../utils/estimacionVideo';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../../../utils/preloadVideosDeTanda';
import type { AjustesDeRedes, DeclaradoDeImagen } from './redes';
import { conMenciones } from '../../mencionesDeAdjuntos';
import { escribirPromptDeImagen } from '../../../../lib/escritorDeImagen';
import { deImagenANombre, deNombreAImagen } from './mencionesDeVideo';
import { armarReceta } from './recetaDeRedes';
import type { ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';

export interface PedidoDeRedes {
  userId: string;
  companyId: string;
  prompt: string;
  adjuntos: ChatAttachment[];
  ajustes: AjustesDeRedes;
  /** La foto de presentación de un modelo guardado, por id; `null` si no tiene. */
  modeloUrl: (modelId: string) => string | null;
  /** El prompt ya es el del escritor ("Mejorar prompt") y quedó igual o casi: el Creador no
   *  lo vuelve a escribir (ADR-2026-10-04-mejorar-prompt-en-redes). En imagen además hace
   *  falta `declarado`: sin él no hay con qué elegir el motor, y se vuelve a escribir. */
  promptEscrito?: boolean;
  /** Imagen: lo que "Mejorar prompt" declaró (tarea, piel, fondo blanco, etiquetas). Viaja
   *  tal cual con el prompt ya escrito, así no se decide dos veces
   *  (ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente). */
  declarado?: DeclaradoDeImagen;
  /** Sólo cuentas dev: el motor de imagen forzado desde la caja
   *  (ADR-2026-10-05-forzar-el-modelo-en-redes). Sólo imagen. */
  proveedorForzado?: ProveedorDeImagen | null;
}

/** Lo que el lienzo necesita para colgar la tanda. */
export interface TandaPedida {
  executionId: string;
  total: number;
  ratio: number;
  esVideo: boolean;
  duracionEstimadaMs?: number;
  /** Ids de `imagenes_generadas`, ya con archivo. */
  resultado: Promise<string[]>;
}

const avisarCreditos = () => {
  // El backend ya descontó los créditos: el pill del shell escucha este evento.
  try {
    window.dispatchEvent(new Event('atlas:credits-updated'));
  } catch {
    /* noop */
  }
};

/** Preload de las filas antes de resolver, como la columna de resultados y el lienzo. */
async function conPreloadDeImagenes(ids: string[], companyId: string): Promise<string[]> {
  try {
    const pre = await preloadGeneratedImages(ids, companyId);
    if (pre.length > 0) return pre.map((x) => String(x.id));
  } catch {
    // Sin preload los ids igual sirven: la miniatura se pide por su cuenta.
  }
  return ids;
}

async function conPreloadDeVideos(ids: string[], companyId: string): Promise<string[]> {
  if (ids.length === 0) return ids;
  try {
    const pre = await preloadVideosDeTanda(ids, companyId);
    if (pre.length > 0) return pre.map((x) => String(x.id));
  } catch {
    /* ídem */
  }
  return ids;
}

const depsDeReferencias = (p: PedidoDeRedes) => ({
  productoUrl: (id: string) => getProductFullImageUrl(p.companyId, id),
  modeloUrl: (id: string) => p.modeloUrl(id),
  adjuntoUrl: (path: string, bucket?: string) => signReferenceImage(path, bucket),
});

// `conMenciones` vive en `mencionesDeAdjuntos` (la comparte la barra de Editar del visor).

/**
 * Una tanda de IMÁGENES: lo mismo que `PromptLibreSheet.handleGenerar`, más el
 * metaprompting del agente. Si el prompt no viene de "Mejorar prompt", el backend lo redacta
 * y elige el motor como paso del job (`escribirPrompt`); si viene, viaja con lo que esa
 * mejora ya declaró y no se vuelve a escribir.
 */
export function pedirImagenEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const promptFinal = p.prompt.trim();
  const { formatSlug, calidad, cantidad } = p.ajustes.imagen;
  // Recientes: el MISMO historial que Prompt libre, así lo escrito en un modo aparece en
  // el otro. Fire-and-forget.
  recordPromptHistory(p.companyId, 'prompt_libre', promptFinal);

  const resultado = (async () => {
    const yaEscrito = p.promptEscrito === true && !!p.declarado;
    const referenceImages = conMenciones(
      await resolverReferencias(p.adjuntos, depsDeReferencias(p)),
      p.adjuntos,
      yaEscrito ? p.declarado?.etiquetas : [],
    );
    const { generateFreeformImage } = await import('../../agentGenerate');
    const { imageIds } = await generateFreeformImage({
      userId: p.userId,
      prompt: promptFinal,
      formatSlug,
      cantidad,
      companyId: p.companyId,
      executionId,
      imageSize: calidad,
      modoUtilizado: 'redes',
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
      ...(yaEscrito && p.declarado
        ? {
            tarea: p.declarado.tarea,
            ...(p.declarado.edicion === 'fusionar' ? { edicion: 'fusionar' as const } : {}),
            ...(p.declarado.pielExpuesta ? { pielExpuesta: true } : {}),
            ...(p.declarado.fondoBlanco ? { fondoBlanco: true, luzFondoBlanco: p.declarado.luzFondoBlanco } : {}),
          }
        : { escribirPrompt: true }),
      // Lo que había en la caja, para "Rehacer" desde la cuadrícula.
      receta: armarReceta(p.prompt, p.adjuntos, p.ajustes),
      ...(p.proveedorForzado ? { providerOverride: p.proveedorForzado } : {}),
    });
    avisarCreditos();
    return conPreloadDeImagenes(imageIds.map(String), p.companyId);
  })();

  return {
    executionId,
    total: cantidad,
    ratio: ratioDelFormatoElegido(formatSlug),
    esVideo: false,
    resultado,
  };
}

/** Una tanda de VIDEO: lo mismo que `CreadorVideoSheet.handleGenerar`, en una función. */
export function pedirVideoEnRedes(p: PedidoDeRedes): TandaPedida {
  const executionId = crypto.randomUUID();
  const promptFinal = p.prompt.trim();
  const { formatSlug, ajustes, tipoVideo } = p.ajustes.video;
  const variantIds = p.adjuntos.flatMap((a) => (a.kind === 'product' ? [a.productId] : []));
  const duracionEstimadaMs = estimarDuracionVideoMs({
    quality: ajustes.quality,
    resolution: ajustes.resolution,
    durationSeconds: ajustes.durationSeconds,
  });
  if (promptFinal) recordPromptHistory(p.companyId, 'video', promptFinal);

  const resultado = (async () => {
    const referenceImages = await resolverReferencias(p.adjuntos, depsDeReferencias(p), MAX_REFERENCIAS_VIDEO);
    // Un VIDEO adjunto (una creación sumada como referencia) viaja aparte, como video de
    // referencia en modo `referencia`: el video nuevo toma de él el estilo, el ritmo y la
    // cámara, y el contenido sale del prompt. Uno solo, que es lo que acepta el proveedor
    // (ADR-2026-10-05-acciones-de-video-en-la-cuadricula-de-redes). Si no se puede firmar,
    // el pedido sale sin él.
    const video = p.adjuntos.find((a) => a.kind === 'video');
    const urlDelVideo = video && video.kind === 'video' ? await signReferenceImage(video.storagePath, video.bucket).catch(() => null) : null;
    const { generateVideoFromPrompt } = await import('../../agentGenerate');
    const { videoId } = await generateVideoFromPrompt({
      userId: p.userId,
      // En la caja las referencias se nombran (`@Remera oversize`); Seedance las conoce
      // por posición (`@Image1`). Se traduce acá, contra las MISMAS referencias que viajan
      // (ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre). El historial de
      // prompts guarda lo que el usuario vio.
      prompt: deNombreAImagen(promptFinal, referenceImages, p.adjuntos),
      formatSlug,
      durationSeconds: ajustes.durationSeconds,
      quality: ajustes.quality,
      resolution: ajustes.resolution,
      companyId: p.companyId,
      executionId,
      modoUtilizado: 'redes',
      tipoVideo,
      ...(p.promptEscrito ? { promptEscrito: true } : {}),
      receta: armarReceta(p.prompt, p.adjuntos, p.ajustes),
      ...(variantIds.length > 0 ? { variantIds } : {}),
      ...(referenceImages.length > 0 ? { referenceImages } : {}),
      ...(urlDelVideo ? { referenceVideos: [{ url: urlDelVideo, modo: 'referencia' as const }] } : {}),
    });
    avisarCreditos();
    return conPreloadDeVideos(idsDeVideo(videoId), p.companyId);
  })();

  return {
    executionId,
    total: 1,
    ratio: ratioDelFormatoElegido(formatSlug),
    esVideo: true,
    duracionEstimadaMs,
    resultado,
  };
}

export const pedirEnRedes = (p: PedidoDeRedes): TandaPedida =>
  p.ajustes.medio === 'video' ? pedirVideoEnRedes(p) : pedirImagenEnRedes(p);

/**
 * "Mejorar prompt" en VIDEO: el escritor del Creador, suelto, con lo mismo que después va
 * a generar (texto, tipo, duración, formato, referencias firmadas y productos). Devuelve el
 * prompt para ponerlo en la caja (ADR-2026-10-04-mejorar-prompt-en-redes). El body se arma
 * como el de `generateVideoFromPrompt` para que la leyenda `@ImageN` numere igual.
 */
async function escribirVideoEnRedes(p: PedidoDeRedes): Promise<string> {
  const { formatSlug, ajustes, tipoVideo } = p.ajustes.video;
  const variantIds = p.adjuntos.flatMap((a) => (a.kind === 'product' ? [a.productId] : []));
  const referencias = await resolverReferencias(p.adjuntos, depsDeReferencias(p), MAX_REFERENCIAS_VIDEO);
  const r = await escribirPromptDeVideo({
    // Las menciones del usuario van como `@ImageN`, que es como el escritor ve la leyenda.
    prompt: deNombreAImagen(p.prompt.trim(), referencias, p.adjuntos),
    tipo_video: tipoVideo,
    duration: ajustes.durationSeconds,
    aspect_ratio: formatSlugToAspect(formatSlug),
    ...(referencias.length > 0
      ? {
          reference_images: referencias.map((r) => ({
            ...(r.url ? { url: r.url } : {}),
            tipo: r.tipo,
            ...(r.tipo === 'producto' && r.productId ? { product_id: r.productId } : {}),
            ...(r.tipo === 'producto' && r.vista ? { vista: r.vista } : {}),
            ...(r.etiqueta ? { etiqueta: r.etiqueta } : {}),
          })),
        }
      : {}),
    ...(variantIds.length > 0 ? { variante_ids: variantIds } : {}),
  });
  // Y lo que escribió vuelve a la caja con nombres: `@Image1` no le dice nada al usuario.
  return deImagenANombre(r.prompt, referencias, p.adjuntos);
}

/**
 * "Mejorar prompt" en IMAGEN: el escritor de imagen del backend, con las reglas del agente.
 * Devuelve el prompt (las fotos siguen nombradas `@Nombre`) y lo que declaró, con las
 * etiquetas de las fotos subidas atadas al id de su adjunto.
 */
async function escribirImagenEnRedes(p: PedidoDeRedes): Promise<{ prompt: string; declarado: DeclaradoDeImagen }> {
  const referencias = conMenciones(await resolverReferencias(p.adjuntos, depsDeReferencias(p)), p.adjuntos);
  const r = await escribirPromptDeImagen({
    prompt: p.prompt.trim(),
    Format: formatSlugToAspect(p.ajustes.imagen.formatSlug),
    ...(referencias.length > 0
      ? {
          reference_images: referencias.map((x) => ({
            ...(x.url ? { url: x.url } : {}),
            tipo: x.tipo,
            ...(x.productId ? { product_id: x.productId } : {}),
            ...(x.etiqueta ? { etiqueta: x.etiqueta } : {}),
            ...(x.mencion ? { mencion: x.mencion } : {}),
          })),
        }
      : {}),
  });
  const porMencion = new Map(referencias.flatMap((x) => (x.mencion && x.adjuntoId ? [[x.mencion.toLowerCase(), x.adjuntoId] as const] : [])));
  return {
    prompt: r.prompt,
    declarado: {
      tarea: r.tarea,
      edicion: r.edicion,
      pielExpuesta: r.piel_expuesta,
      fondoBlanco: r.fondo_blanco,
      luzFondoBlanco: r.luz_fondo_blanco,
      etiquetas: r.etiquetas.flatMap((e) => {
        const adjuntoId = porMencion.get(e.mencion.replace(/^@/, '').toLowerCase());
        return adjuntoId ? [{ adjuntoId, etiqueta: e.etiqueta }] : [];
      }),
    },
  };
}

/** "Mejorar prompt": el escritor del medio activo. En imagen, además, lo que declaró. */
export async function escribirPromptEnRedes(p: PedidoDeRedes): Promise<{ prompt: string; declarado?: DeclaradoDeImagen }> {
  return p.ajustes.medio === 'video' ? { prompt: await escribirVideoEnRedes(p) } : escribirImagenEnRedes(p);
}
